<template>
  <mi-dialog
    no-backdrop-dismiss
    :model-value="modelValue"
    custom-design-dialog
    class="bom-send-to-search-modal"
    @before-show="getElements(tarFilterKvsList)"
  >
    <div class="bom-send-to-search-modal-header">
      <h3 class="bom-send-to-search-modal-header-title q-my-sm q-px-none">
        Select elements to send
      </h3>
      <mi-btn
        v-close-popup
        icon="close"
        icon-type
        icon-size="18px"
        fab
        flat
        @click="toggleOpenModal(false )"
      ></mi-btn>
    </div>
    <div class="bom-send-to-search-modal-content">
      <div
        v-if="showWarningTarBanner"
        class="bom-send-to-search-modal-warning"
      >
        <bom-validation-results-elements-info
          v-show="true"
          :info-data="BOM_VALIDATION_TAR_FILTER_WARNING"
        >
          <template #action>
            <mi-btn v-if="!isFetchingElements" flat @click="onHandleDisplayAllElements">
              {{ BOM_VALIDATION_TAR_FILTER_WARNING.btnText }}
            </mi-btn>
          </template>
        </bom-validation-results-elements-info>
      </div>
      <div class="bom-send-to-search-modal-subtitle">
        <div class="bom-send-to-search-modal-subtitle__caption">
          <span class="bom-send-to-search-modal-subtitle__title">
            Product model
          </span>
          <span class="bom-send-to-search-modal-subtitle__value">
            {{ isUserExports ?
              bomValidationResults?.userExportsName :
              bomValidationResults?.businessName }}
          </span>
        </div>
        <div class="bom-send-to-search-modal-subtitle__caption">
          <span class="bom-send-to-search-modal-subtitle__title">
            Planning period
          </span>
          <span class="bom-send-to-search-modal-subtitle__value">
            {{ bomValidationResults.planningPeriodRange.from }}
          </span>
        </div>
        <div class="bom-send-to-search-modal-subtitle__caption">
          <span class="bom-send-to-search-modal-subtitle__title">
            Problem type
          </span>
          <span class="bom-send-to-search-modal-subtitle__value">
            {{ problemType }}
          </span>
        </div>
      </div>
      <div v-if="isFetchingElements" class="bom-send-to-search-modal-elements__loading">
        <mi-spinner size="48px" class="q-mb-sm"></mi-spinner>
        <span>Fixing elements to be available in product model.</span>
      </div>
      <div v-else class="bom-send-to-search-modal-elements">
        <elements-tab-container
          v-slot="props"
          :items="filteredElements"
          :selected-world-type="selectedWorldType"
          :update-selected-world-type="updateSelectedWorldType"
        >
          <search-results-table
            v-model="searchString"
            :items="filteredElements[selectedWorldType]"
            :header-color="props.worldType.color"
            :show-no-data-found="!elementsToShow[selectedWorldType]?.length"
            :product-model-change="true"
          >
            <template #default="{ item }">
              <search-results-group
                :key="item.element.id"
                :item="item.element"
                :color="props.worldType.color"
                :selected-elements-ids="selectedAddedElementsIds"
                :handle-item-click="handleItemClick"
                :handle-sub-item-click="handleSubItemClick"
              >
                <template #custom-right-header>
                  <div
                    v-if="selectedWorldType === SEARCH_RESULT_TYPES.OPTIONS"
                    class="bom-send-to-search-modal-view-mode"
                  >
                    <span class="counter">
                      {{ getSelectedSubElementsCount(item.element.id) }} /
                      {{ getTotalSubElementsCount(item.element.id) }}
                    </span>
                    <q-btn-toggle
                      v-model="item.isFull"
                      :options="[
                        { label: 'Partial', value: 'partial', slot: 'partial' },
                        { label: 'Full', value: 'full' ,slot: 'full' }
                      ]"
                      unelevated
                      rounded
                      text-color="primary"
                      color="white"
                      toggle-text-color="primary"
                      toggle-color="yellow-14"
                      no-caps
                      class="segmented"
                      @click.stop="changeElement(item)"
                    >
                      <template #partial>
                        <mi-tooltip anchor="top middle" self="bottom right">Found in validation</mi-tooltip>
                      </template>

                      <template #full>
                        <mi-tooltip anchor="top middle" self="bottom right">Found in product model</mi-tooltip>
                      </template>
                    </q-btn-toggle>
                  </div>
                </template>
              </search-results-group>
            </template>
          </search-results-table>
        </elements-tab-container>
      </div>
      <div class="bom-send-to-search-modal-actions">
        <mi-btn flat class="q-mr-sm" @click="toggleOpenModal(false)">Cancel</mi-btn>
        <mi-btn
          :disable="isFetchingElements || noSelectedElements"
          @click="onHandleSendToSearch"
        >
          Send to search
        </mi-btn>
      </div>
    </div>
  </mi-dialog>
</template>

<script>
  import MiDialog from '@/packages/@mi-library/MiDialog/MiDialog.vue'
  import { computed, getCurrentInstance, ref } from 'vue'
  import {
    BOM_VALIDATION_TAR_FILTER_WARNING, PM_TYPE,
    SEARCH_RESULT_TYPES
  } from '@/constants'
  import ElementsTabContainer from '@/components/product-model/elements-change/ElementsTabContainer.vue'
  import SearchResultsGroup from '@/components/search/search-results/SearchResultsGroup.vue'
  import SearchResultsTable from '@/components/search/search-results/SearchResultsTable.vue'
  import { getBOMValidationElements, postSummary } from '@/api'
  import MiSpinner from '@/packages/@mi-library/MiSpinner/MiSpinner.vue'
  import { SEARCH_ROUTE } from '@/router/routeNames'
  import notify from '@/utils/notify'
  import { useStore } from 'vuex'
  import { useRouter } from 'vue-router'
  import { filterElementsByString } from '@/components/product-model/utils'
  import BomValidationResultsElementsInfo from
    '@/components/validations/bom/validation-results/elements/BomValidationResultsElementsInfo.vue'
  import MiTooltip from '@/packages/@mi-library/MiTooltip/MiTooltip.vue'
  import cloneDeep from 'lodash.clonedeep'

  export default {
    name: 'BomValidationResultsElementsSendToSearchModal',
    components: {
      MiTooltip,
      BomValidationResultsElementsInfo,
      MiSpinner,
      SearchResultsTable,
      SearchResultsGroup,
      ElementsTabContainer,
      MiDialog
    },
    props: {
      modelValue: {
        type: Boolean,
        required: true
      },
      bomValidationResults: {
        type: Object,
        required: true,
        default: () => {}
      },
      problemType: {
        type: String,
        required: true
      },
      toggleOpenModal: {
        type: Function,
        required: true
      },
      tarFilterKvsList: {
        type: Array,
        required: true
      }
    },
    setup(props) {
      const selectedWorldType = ref(SEARCH_RESULT_TYPES.OPTIONS)
      const selectedAddedElementsIds = ref({
        [SEARCH_RESULT_TYPES.OPTIONS]: new Map(),
        [SEARCH_RESULT_TYPES.COMPONENTS]: new Map()
      })
      const { proxy } = getCurrentInstance()
      const router = useRouter()
      const VIEW_TYPES = ref({ FULL: 'full', PARTIAL: 'partial' })
      const store = useStore()
      const searchString = ref('')
      const isFetchingElements = ref(false)
      const isSendToSearchLoading = ref(false)
      const showWarningTarBanner = ref(false)
      const isUserExports = computed(() => props.bomValidationResults?.productModelType === PM_TYPE.USER_EXPORTS)
      const fullElements = ref(ref({ options: [], components: [] }))
      const partialElements = ref(ref({ options: [], components: [] }))

      const elementsToShow = ref({ options: [], components: [] })
      const filteredElements = computed(() => ({
        options: filterElementsByString(elementsToShow.value.options, searchString.value),
        components: filterElementsByString(elementsToShow.value.components, searchString.value)
      }))

      const updateSelectedWorldType = worldTypeKey => {
        selectedWorldType.value = worldTypeKey
      }

      const noSelectedElements = computed(() => selectedAddedElementsIds.value.options.size === 0
        && selectedAddedElementsIds.value.components.size === 0)

      const handleItemClick = (elementTypes, { item = {}, isChosen = false } = {}) => {
        if (!isChosen) {
          const subItensIds = item[elementTypes.subItem]?.map(subItem => subItem.id) || []
          selectedAddedElementsIds.value[elementTypes.item].set(item.id, subItensIds)
        }
        else {
          selectedAddedElementsIds.value[elementTypes.item].delete(item.id)
        }
      }

      const handleSubItemClick = (elementTypes, { subItem = {}, isChosen = false } = {}) => {
        const map = selectedAddedElementsIds.value[elementTypes.item] // options ou components

        const itemId = elementTypes.item === SEARCH_RESULT_TYPES.OPTIONS
          ? subItem.optionId
          : subItem.componentId

        if (!map.has(itemId)) {
          map.set(itemId, [])
        }

        const subItemsIds = map.get(itemId)

        if (!isChosen && !subItemsIds.includes(subItem.id)) {
          subItemsIds.push(subItem.id)
        }
        else {
          const index = subItemsIds.indexOf(subItem.id)
          if (index !== -1) {
            subItemsIds.splice(index, 1)

            if (subItemsIds.length <= 0) {
              map.delete(itemId)
            }
          }
        }
      }

      const buildParams = (partialElementsList = false) => {
        const params = new URLSearchParams()
        params.append('elementTypes', props.problemType.toUpperCase())

        if (props.bomValidationResults?.modified) {
          params.append('newProductModelId', props.bomValidationResults.productModelId)
        }

        if (partialElementsList) {
          params.append('choiceList', VIEW_TYPES.value.PARTIAL.toUpperCase())
        }

        return params
      }

      const selectAllElements = () => {
        elementsToShow.value.options.forEach(option => handleItemClick(
          { item: SEARCH_RESULT_TYPES.OPTIONS, subItem: SEARCH_RESULT_TYPES.CHOICES },
          { item: option.element, isChosen: false }
        ))
        elementsToShow.value.components.forEach(component => handleItemClick(
          { item: SEARCH_RESULT_TYPES.COMPONENTS, subItem: SEARCH_RESULT_TYPES.COMPONENT_VARIANTS },
          { item: component.element, isChosen: false }
        ))
      }

      const getElements = async (kvsList = []) => {
        isFetchingElements.value = true

        try {
          let params = buildParams(false)
          let data = await getBOMValidationElements(
            props.bomValidationResults.id, params, kvsList
          )

          fullElements.value = {
            options: data.options.map(option => ({ element: option, isFull: VIEW_TYPES.value.FULL })),
            components: data.components.map(component => ({ element: component, isFull: VIEW_TYPES.value.FULL }))
          }

          elementsToShow.value = cloneDeep(fullElements.value)

          selectAllElements()

          params = buildParams(true)
          data = await getBOMValidationElements(
            props.bomValidationResults.id, params, kvsList
          )

          partialElements.value = {
            options: data.options.map(option => ({ element: option, isFull: VIEW_TYPES.value.PARTIAL })),
            components: data.components.map(component => ({ element: component, isFull: VIEW_TYPES.value.PARTIAL }))
          }

          showWarningTarBanner.value = kvsList.length > 0
        }
        catch (error) {
          notify({
            title: 'Error',
            content: 'Failed loading elements.',
            type: 'negative',
            progress: true
          })
        }
        finally {
          isFetchingElements.value = false
        }
      }

      const emptySummaryElements = () => {
        store.commit('search/REPLACE_SELECTED_ELEMENTS', {
          elementsType: SEARCH_RESULT_TYPES.OPTIONS,
          subElementsType: SEARCH_RESULT_TYPES.CHOICES,
          elements: []
        })
        store.commit('search/REPLACE_SELECTED_ELEMENTS', {
          elementsType: SEARCH_RESULT_TYPES.COMPONENTS,
          subElementsType: SEARCH_RESULT_TYPES.COMPONENT_VARIANTS,
          elements: []
        })
      }

      const setToSearch = (key, value) => {
        proxy.$q.localStorage.remove(key)
        proxy.$q.localStorage.set(key, value)
      }

      const normalize = list => list.map(listElement => listElement.element)

      const filterBySelected = (completeList, selectedIdsMap, subElementType) => completeList
        .filter(item => selectedIdsMap?.has(item.id))
        .map(item => {
          const selectedIds = selectedIdsMap.get(item.id) || []

          return {
            ...item,
            [subElementType]: (item[subElementType] || []).filter(subElement => selectedIds.includes(subElement.id))
          }
        })

      const normalizeAndFilterElements = () => {
        const normalizedOptions = normalize(elementsToShow.value.options)
        const normalizedComponents = normalize(elementsToShow.value.components)

        const filteredOptions = filterBySelected(
          normalizedOptions, selectedAddedElementsIds.value.options, SEARCH_RESULT_TYPES.CHOICES
        )

        const filteredComponents = filterBySelected(
          normalizedComponents, selectedAddedElementsIds.value.components, SEARCH_RESULT_TYPES.COMPONENT_VARIANTS
        )

        return {
          options: filteredOptions,
          components: filteredComponents
        }
      }

      const onHandleSendToSearch = async () => {
        try {
          isSendToSearchLoading.value = true

          const { options, components } = normalizeAndFilterElements()

          emptySummaryElements()

          const pmEncodedBusinessName = isUserExports.value
            ? atob(props.bomValidationResults.encodedBusinessName)
            : props.bomValidationResults.encodedBusinessName

          await postSummary({
            name: `${ store.state.user.username }_CurrentSelection`,
            pmEncodedBusinessName,
            options,
            components,
            currentSelection: true,
            collection: null,
            productModelId: props.bomValidationResults.productModelId
          })

          const planningPeriod = {
            to: props.bomValidationResults.planningPeriodRange?.from,
            from: props.bomValidationResults.planningPeriodRange?.to
          }

          setToSearch(process.env.VUE_APP_STORAGE_KEY_PLANNING_PERIOD, planningPeriod)

          const productModel = {
            id: props.bomValidationResults.productModelId,
            encodedBusinessName: pmEncodedBusinessName,
            productModelType: props.bomValidationResults.productModelType
          }

          setToSearch(process.env.VUE_APP_STORAGE_KEY_VM_PRODUCT_MODEL, productModel)

          await router.push({ name: SEARCH_ROUTE.name })
        }
        catch (error) {
          notify({
            title: 'Error',
            content: 'Failed sending elements to search.',
            type: 'negative',
            progress: true
          })
        }
        finally {
          isSendToSearchLoading.value = false
        }
      }

      const onHandleDisplayAllElements = async () => {
        await getElements()
      }

      const updateSelectedElements = () => {
        const subElementType = selectedWorldType.value === SEARCH_RESULT_TYPES.OPTIONS
          ? SEARCH_RESULT_TYPES.CHOICES
          : SEARCH_RESULT_TYPES.COMPONENT_VARIANTS

        const elementIds = elementsToShow.value[selectedWorldType.value].map(item => item.element.id)
        const map = selectedAddedElementsIds.value[selectedWorldType.value]

        for (const elementId of map.keys()) {
          if (!elementIds.includes(elementId)) {
            map.delete(elementId)
          }
          else {
            const element = elementsToShow.value[selectedWorldType.value].find(item => item.element.id === elementId)
            const availableSubElementIds = element.element[subElementType]?.map(subElement => subElement.id) || []
            const selectedIds = map.get(elementId) || []
            const keptIds = selectedIds.filter(id => availableSubElementIds.includes(id))

            if (keptIds.length === 0) {
              map.delete(elementId)
            }
            else {
              map.set(elementId, keptIds)
            }
          }
        }
      }

      const changeElement = element => {
        const changedList = element.isFull === VIEW_TYPES.value.FULL ? fullElements : partialElements

        const newItem = changedList.value[selectedWorldType.value].find(
          item => item?.element?.id === element.element.id
        )

        elementsToShow.value[selectedWorldType.value] = elementsToShow.value[selectedWorldType.value].map(
          item => (item?.element?.id === element.element.id ? { ...newItem, isFull: item?.isFull } : item)
        )

        updateSelectedElements()
      }

      const getSelectedSubElementsCount = elementId => {
        const map = selectedAddedElementsIds.value?.[selectedWorldType.value]

        if (!map) return 0

        const selectedSubElements = map.get(elementId)

        return selectedSubElements?.length ?? 0
      }

      const getTotalSubElementsCount = elementId => {
        const subElementType = selectedWorldType.value === SEARCH_RESULT_TYPES.OPTIONS ? SEARCH_RESULT_TYPES.CHOICES
          : SEARCH_RESULT_TYPES.COMPONENT_VARIANTS

        const list = filteredElements.value?.[selectedWorldType.value]

        if (!list) return 0

        const found = list.find(
          item => item.element?.id === elementId
        )

        return found?.element?.[subElementType]?.length ?? 0
      }

      return {
        onHandleDisplayAllElements,
        selectedWorldType,
        updateSelectedWorldType,
        showWarningTarBanner,
        getSelectedSubElementsCount,
        getTotalSubElementsCount,
        changeElement,
        handleSubItemClick,
        handleItemClick,
        selectedAddedElementsIds,
        elementsToShow,
        noSelectedElements,
        searchString,
        filteredElements,
        isFetchingElements,
        getElements,
        onHandleSendToSearch,
        isUserExports,
        normalizeAndFilterElements,
        buildParams,
        partialElements,
        fullElements
      }
    },
    computed: {
      SEARCH_RESULT_TYPES() {
        return SEARCH_RESULT_TYPES
      },
      PM_TYPE() {
        return PM_TYPE
      },
      BOM_VALIDATION_TAR_FILTER_WARNING() {
        return BOM_VALIDATION_TAR_FILTER_WARNING
      }
    }
  }
</script>

<style lang="scss">
  // stylelint-disable declaration-no-important
  .bom-send-to-search-modal-view-mode {
    display: flex;
    flex-direction: row;
    gap: 16px;
    align-items: center;

    .counter {
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      font-size: 12px;
      line-height: 130%;
      vertical-align: middle;
      color: #303c49;
      letter-spacing: 0;
    }

    .segmented {
      background-color: $mi-white;
      padding: 4px;
      width: 125px !important;
      height: 32px !important;
      gap: 4px;
    }

    .q-btn-group > .q-btn-item:not(:first-child),
    .q-btn-group > .q-btn-item:not(:last-child) {
      border-radius: 20px !important;
      height: 20px !important;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      justify-content: center;
      font-size: 11.5px;
      line-height: 130%;
      align-self: center;
      letter-spacing: 0;
      vertical-align: middle;
    }
  }

  .bom-send-to-search-modal-dialog .q-dialog__inner > .mi-card {
    width: 924px;
    max-width: 924px !important;
    min-width: 924px !important;
    position: relative;
  }

  .bom-send-to-search-modal-header {
    background-color: $mi-anthracite-50;
    height: 90px;
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    margin: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
  }

  .bom-send-to-search-modal-warning {
    padding: 0 16px 16px;
  }

  .bom-send-to-search-modal-subtitle {
    display: flex;
    flex-direction: row;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0;
    color: #303c49;
    height: 48px;
    font-family: $mi-typography-font-family-condensed;

    &__caption {
      border: 1px solid $mi-anthracite-50;
      padding: 16px 0 16px 24px;
      width: 360px;
    }

    &__title {
      font-weight: 700;
    }

    &__value {
      font-weight: 400;
    }
  }

  .bom-send-to-search-modal-elements {
    height: 585px;

    &__loading {
      height: 585px;
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;
    }
  }

  .bom-send-to-search-modal-actions {
    height: 90px;
    align-content: center;
    justify-self: right;
    padding: 24px;
  }

  .bom-send-to-search-modal-header-title {
    font-weight: 700;
    color: $mi-anthracite-800;
  }

  @media (min-width: 600px) {
    .q-dialog__inner--minimized > div {
      max-width: 100% !important;
    }
  }
</style>
